import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Pencil, Trash2, ExternalLink, Link as LinkIcon, CheckCircle2, Eye } from "lucide-react";
import { motion } from "framer-motion";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";

function fmtWhen(s) {
  if (!s) return "";
  return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/reports");
    setReports(data.reports || []);
  }, []);

  useEffect(() => { if (user && ["admin", "regional"].includes(user.role)) load(); }, [user, load]);

  if (user && !["admin", "regional"].includes(user.role)) return <Navigate to="/" replace />;

  const createReport = async () => {
    setCreating(true);
    try {
      const { data: meta } = await api.get("/financials/meta");
      const latest = meta.latest_period || new Date().toISOString().slice(0, 7);
      const { data } = await api.post("/reports", {
        title: "Untitled Report", intro: "", location: "all",
        start: latest, end: latest, compare: "prior",
        projects: [], goals: [], sections: [], images: [],
      });
      navigate(`/reports/${data.id}`);
    } catch (err) {
      toast.error("Could not create report", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setCreating(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/reports/${r.id}`); toast.success("Report deleted"); load(); }
    catch (err) { toast.error("Delete failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const copyLink = async (r) => {
    const url = `${window.location.origin}/r/${r.share_token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied", { description: url }); }
    catch { toast.error("Couldn't copy the link"); }
  };

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1100px] mx-auto px-6 lg:px-12 py-10 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Admin · Reporting</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Reports</h1>
            <p className="text-sm text-brand-muted mt-1">Build shareable investor reports from your numbers plus your own narrative.</p>
          </div>
          <button onClick={createReport} disabled={creating} data-testid="report-create"
            className="flex items-center gap-2 bg-brand-primary text-brand-page px-5 py-2.5 text-sm hover:bg-brand-primaryHover active:scale-[0.98] transition-all disabled:opacity-60">
            <Plus className="h-4 w-4" /> {creating ? "Creating…" : "New Report"}
          </button>
        </div>

        {reports === null ? (
          <div className="py-20 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
        ) : reports.length === 0 ? (
          <div className="bg-brand-surface border border-brand-border px-6 py-20 text-center" data-testid="reports-empty">
            <FileText className="h-8 w-8 text-brand-tertiary mx-auto mb-4" />
            <p className="font-serif text-2xl text-brand-ink">No reports yet</p>
            <p className="text-sm text-brand-muted mt-1.5">Create your first end-of-quarter report to share with investors.</p>
          </div>
        ) : (
          <div className="bg-brand-surface border border-brand-border divide-y divide-brand-border" data-testid="reports-list">
            {reports.map((r, i) => {
              const published = r.status === "published";
              return (
                <motion.div key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }}
                  data-testid={`report-row-${r.id}`} className="flex items-center justify-between gap-4 px-6 py-5 hover:bg-brand-hover group">
                  <Link to={`/reports/${r.id}`} className="min-w-0 flex-1">
                    <div className="flex items-center gap-3">
                      <p className="font-serif text-xl text-brand-ink truncate">{r.title || "Untitled Report"}</p>
                      <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5 ${published ? "text-brand-pos bg-brand-pos/10" : "text-brand-gold bg-brand-gold/10"}`}>
                        {published && <CheckCircle2 className="h-3 w-3" />}{published ? "Published" : "Draft"}
                      </span>
                      <span data-testid={`report-views-${r.id}`} title={`${r.unique_viewers || 0} unique viewer(s)`}
                        className={`shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5 ${r.view_count ? "text-brand-primary bg-brand-primary/10" : "text-brand-tertiary bg-brand-hover"}`}>
                        <Eye className="h-3 w-3" />{r.view_count || 0} view{r.view_count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <p className="text-xs text-brand-tertiary mt-1">{r.location_label} · {r.period_label} · updated {fmtWhen(r.updated_at)}{r.last_viewed ? ` · last opened ${fmtWhen(r.last_viewed)}` : ""}</p>
                  </Link>
                  <div className="flex items-center gap-1 shrink-0">
                    {published && (
                      <>
                        <button onClick={() => copyLink(r)} data-testid={`report-copy-${r.id}`} title="Copy share link" className="p-2 text-brand-tertiary hover:text-brand-primary"><LinkIcon className="h-4 w-4" /></button>
                        <a href={`/r/${r.share_token}`} target="_blank" rel="noreferrer" data-testid={`report-open-${r.id}`} title="Open shared report" className="p-2 text-brand-tertiary hover:text-brand-primary"><ExternalLink className="h-4 w-4" /></a>
                      </>
                    )}
                    <Link to={`/reports/${r.id}`} data-testid={`report-edit-${r.id}`} title="Edit" className="p-2 text-brand-tertiary hover:text-brand-primary"><Pencil className="h-4 w-4" /></Link>
                    <button onClick={() => remove(r)} data-testid={`report-delete-${r.id}`} title="Delete" className="p-2 text-brand-tertiary hover:text-brand-neg"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
